import { inject, singleton } from 'tsyringe';
import {
  kNameOfUserActionService,
  IUserActionService,
  IUserActionServiceDelegate,
} from '../interfaces/user-action-service.interface';
import { INotificationDisplayDetails } from 'app-core/src/models/notification-display-details.model';
import { TrackEvent } from 'app-core/src/models/tracking-event.model';
import { TimerService } from './timer.service';
import { ABTestParams } from 'app-core/src/services/notification-analytics/models/ab-test-params.model';
import { NotificationAnalyticsService } from 'app-core/src/services/notification-analytics/notification-analytics.service';
import { kNameOfDownloadService } from 'app-core/src/constants/constants';
import { IDownloadService } from 'app-core/src/interfaces/download-service.interface';
import { IDownloadServiceDelegate } from 'app-core/src/interfaces/download-service-delegate.interface';
import { ContentRecommendationUserAction } from 'app-core/src/constants/content-recommendation-user-action.enum';
import {
  kDIOWIOPathsWrapper,
  kDITOWCustomURLWrapper,
  kDITOWExtensionWrapper,
  kDITOWUtilsWrapper,
} from 'app-core/src/constants/injectionTokens';
import { IOWUtilsWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-utils-wrapper.interface';
import { IOWExtensionWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-extension-wrapper.interface';
import { FunctionPropertyNames } from '../types';
import { IOWCustomURLWrapper } from '../interfaces/ow-wrappers/ow-custom-url-wrapper.interface';
import { IOWIOPathsWrapper } from '../interfaces/ow-wrappers/ow-io-paths-wrapper.interface';

// -----------------------------------------------------------------------------
@singleton()
export class UserActionService
  implements IUserActionService, IDownloadServiceDelegate
{
  private readonly listeners: Set<IUserActionServiceDelegate>;

  // ---------------------------------------------------------------------------
  public constructor(
    @inject(kDITOWExtensionWrapper)
    private readonly owExtensionWrapper: IOWExtensionWrapper,
    @inject(kDITOWUtilsWrapper)
    private readonly owUtilsWrapper: IOWUtilsWrapper,
    private readonly notificationAnalyticsService: NotificationAnalyticsService,
    @inject(kDITOWCustomURLWrapper)
    private readonly owCustomURLWrapper: IOWCustomURLWrapper,
    private readonly timerService: TimerService,
    @inject(kNameOfDownloadService)
    private readonly downloadService: IDownloadService,
    @inject(kDIOWIOPathsWrapper)
    private readonly ioPaths: IOWIOPathsWrapper,
  ) {
    this.listeners = new Set<IUserActionServiceDelegate>();
    this.owExtensionWrapper.initialize();
    this.downloadService.addListener(this);
  }

  // ---------------------------------------------------------------------------
  // ExposableService
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  get token(): string {
    return kNameOfUserActionService;
  }

  // ---------------------------------------------------------------------------
  private triggerListenerEvent(
    eventName: FunctionPropertyNames<IUserActionServiceDelegate>,
    param: any,
  ) {
    if (!eventName) {
      return;
    }

    for (const listener of this.listeners) {
      if (!listener[eventName]) {
        continue;
      }

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      listener[eventName](param);
    }
  }

  // ---------------------------------------------------------------------------
  // DelegateProvider
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  addListener(listener: IUserActionServiceDelegate) {
    this.listeners.add(listener);
  }

  // ---------------------------------------------------------------------------
  removeListener(listener: IUserActionServiceDelegate) {
    this.listeners.delete(listener);
  }

  // ---------------------------------------------------------------------------
  // userActionService
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async installApplication(notificationInfo: INotificationDisplayDetails) {
    this.notificationAnalyticsService.reportContentRecommend(
      notificationInfo.targetGameId || notificationInfo.gameId,
      notificationInfo.applicationUid,
      ContentRecommendationUserAction.DirectInstallApplication,
      undefined,
      notificationInfo.originalGameId,
    );

    await this.owExtensionWrapper.installAnyApp(
      notificationInfo.applicationUid,
    );
  }

  // ---------------------------------------------------------------------------
  async openApplicationInStore(notificationInfo: INotificationDisplayDetails) {
    this.notificationAnalyticsService.reportContentRecommend(
      notificationInfo.targetGameId || notificationInfo.gameId,
      notificationInfo.applicationUid,
      ContentRecommendationUserAction.OpenApplicationInStore,
      undefined,
      notificationInfo.originalGameId,
    );

    await this.owExtensionWrapper.addTemporarilyWhitelistedExtension(
      notificationInfo.applicationUid,
    );

    this.owUtilsWrapper.openStoreOneAppPage(notificationInfo.applicationUid);
  }

  // ---------------------------------------------------------------------------
  async openNotificationSettings(notifInfo: INotificationDisplayDetails) {
    this.notificationAnalyticsService.reportContentRecommend(
      notifInfo.targetGameId || notifInfo.gameId,
      notifInfo.applicationUid,
      ContentRecommendationUserAction.NotificationSettingsOpened,
      undefined,
      notifInfo.originalGameId,
    );

    this.owCustomURLWrapper.openNotificationsSettings();
  }

  // ---------------------------------------------------------------------------
  async turnOffNotifications(notificationInfo: INotificationDisplayDetails) {
    this.notificationAnalyticsService.reportContentRecommend(
      notificationInfo.targetGameId || notificationInfo.gameId,
      notificationInfo.applicationUid,
      ContentRecommendationUserAction.TurnOffNotificationsRequested,
      undefined,
      notificationInfo.originalGameId,
    );

    await this.timerService.sleep(100);

    const { manifest } = this.owExtensionWrapper;
    await this.owExtensionWrapper.changeExtensionEnableState(
      manifest.UID,
      false,
    );
  }

  // ---------------------------------------------------------------------------
  sendTrackingEvent(event: TrackEvent) {
    this.notificationAnalyticsService.reportContentViewEvent(event);
  }

  // ---------------------------------------------------------------------------
  sendErrorTrackingEvent(notificationInfo: INotificationDisplayDetails) {
    this.notificationAnalyticsService.reportContentViewError(
      notificationInfo.gameId,
      notificationInfo.applicationUid,
      notificationInfo.originalGameId,
    );
  }

  // ---------------------------------------------------------------------------
  notificationDismissed(
    notificationInfo: INotificationDisplayDetails,
    visibilityTimeInSeconds: number,
  ) {
    this.notificationAnalyticsService.reportContentRecommend(
      notificationInfo.targetGameId || notificationInfo.gameId,
      notificationInfo.applicationUid,
      ContentRecommendationUserAction.Dismissed,
      visibilityTimeInSeconds,
      notificationInfo.originalGameId,
    );
    this.triggerListenerEvent('onUserActionDismissed', notificationInfo);
  }

  // ---------------------------------------------------------------------------
  notificationDismissedWhileDownloading(
    notificationInfo: INotificationDisplayDetails,
    visibilityTimeInSeconds: number,
  ) {
    this.notificationAnalyticsService.reportContentRecommend(
      notificationInfo.targetGameId || notificationInfo.gameId,
      notificationInfo.applicationUid,
      ContentRecommendationUserAction.CloseClickedWhileDownloadingExternalApp,
      visibilityTimeInSeconds,
      notificationInfo.originalGameId,
    );
    this.triggerListenerEvent('onUserActionDismissed', notificationInfo);
  }

  // ---------------------------------------------------------------------------
  gameExcluded(gameId: number) {
    this.triggerListenerEvent('onGameExcluded', gameId);
  }

  // ---------------------------------------------------------------------------
  applicationExcluded(extensionId: string) {
    this.triggerListenerEvent('onApplicationExcluded', extensionId);
  }

  // ---------------------------------------------------------------------------
  notificationABTest(params: ABTestParams) {
    this.notificationAnalyticsService.reportNotificationABTest(params);
  }

  // ---------------------------------------------------------------------------
  OpenExternalUrl(
    url: string,
    activeNotificationInfo: INotificationDisplayDetails,
  ) {
    this.notificationAnalyticsService.reportContentRecommend(
      activeNotificationInfo.targetGameId || activeNotificationInfo.gameId,
      activeNotificationInfo.applicationUid,
      ContentRecommendationUserAction.OpenExternalUrl,
      undefined,
      activeNotificationInfo.originalGameId,
    );
    this.owUtilsWrapper.openUrlInDefaultBrowser(url);
  }

  // ---------------------------------------------------------------------------
  OnDownloadExternalApplication(
    downloadAppUrl: string,
    activeNotificationInfo: INotificationDisplayDetails,
  ) {
    this.notificationAnalyticsService.reportContentRecommend(
      activeNotificationInfo.targetGameId || activeNotificationInfo.gameId,
      activeNotificationInfo.applicationUid,
      ContentRecommendationUserAction.DownloadExternalApp,
      undefined,
      activeNotificationInfo.originalGameId,
    );

    this.downloadService.downloadFile(
      downloadAppUrl,
      `${this.ioPaths.tempLocalAppData}\\${
        activeNotificationInfo.applicationUid
      }_${Date.now()}.exe`,
      activeNotificationInfo.applicationUid,
    );
  }

  // ---------------------------------------------------------------------------
  OnCancelDownloadExternalApplication(
    activeNotificationInfo: INotificationDisplayDetails,
  ) {
    this.notificationAnalyticsService.reportContentRecommend(
      activeNotificationInfo.targetGameId || activeNotificationInfo.gameId,
      activeNotificationInfo.applicationUid,
      ContentRecommendationUserAction.CancelDownloadExternalApp,
      undefined,
      activeNotificationInfo.originalGameId,
    );
    this.downloadService.cancelDownload();
  }

  // ---------------------------------------------------------------------------
  // IDownloadServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  onDownloadStart(url: string) {
    this.triggerListenerEvent('onDownloadStarted', url);
    this.sendTrackingEvent({
      name: 'crn_download_start',
      extra: {
        downloadUrl: url,
        crnAppId: this.downloadService?.downloadingAppid,
      },
      overrides: {},
    });
  }

  // ---------------------------------------------------------------------------
  onDownloadError(reason: string) {
    this.triggerListenerEvent('onDownloadError', reason);
    this.sendTrackingEvent({
      name: 'crn_download_error',
      extra: {
        error: reason,
        url: this.downloadService?.downloadingUrl ?? '',
        crnAppId: this.downloadService?.downloadingAppid,
      },
      overrides: {},
    });
  }

  // ---------------------------------------------------------------------------
  onDownloadCompleted(url: string) {
    this.triggerListenerEvent('onDownloadCompleted', url);
    this.sendTrackingEvent({
      name: 'crn_download_complete',
      extra: { url, crnAppId: this.downloadService?.downloadingAppid },
      overrides: {},
    });
  }

  // ---------------------------------------------------------------------------
  onDownloadProgress(progress: string) {
    this.triggerListenerEvent('onDownloadProgress', progress);
  }

  // ---------------------------------------------------------------------------
  onFileExecuted(result) {
    this.triggerListenerEvent('onFileExecuted', result);
    if (result) {
      return;
    }

    this.sendTrackingEvent({
      name: 'crn_download_verification_error',
      extra: { downloadUrl: this.downloadService.downloadingUrl },
      overrides: {},
    });
  }
}
